/**
 * WCAG 2.1 contrast helpers — used to pick readable text on user-chosen colours
 * (pipeline stage colours, form brand colours).
 */

const LIGHT = '#ffffff';
const DARK = '#000000';

const EXPANDED_SHORTHAND = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const FULL_HEX = /^#?([0-9a-f]{6})([0-9a-f]{2})?$/i;

/**
 * Parse a hex colour (#rgb, #rrggbb or #rrggbbaa) into channels. Returns null when unparseable.
 */
export function parseHexColor(color) {
  if (typeof color !== 'string') return null;

  const value = color.trim();
  const shorthand = value.match(EXPANDED_SHORTHAND);
  if (shorthand) {
    const [, r, g, b] = shorthand;
    return {
      r: parseInt(r + r, 16),
      g: parseInt(g + g, 16),
      b: parseInt(b + b, 16),
    };
  }

  const full = value.match(FULL_HEX);
  if (!full) return null;

  const hex = full[1];
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

const toLinear = (channel) => {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/**
 * Relative luminance per WCAG 2.1, 0 (black) to 1 (white). Returns null when unparseable.
 */
export function relativeLuminance(color) {
  const rgb = parseHexColor(color);
  if (!rgb) return null;

  return 0.2126 * toLinear(rgb.r) + 0.7152 * toLinear(rgb.g) + 0.0722 * toLinear(rgb.b);
}

/**
 * Contrast ratio between two colours, 1 (identical) to 21 (black on white).
 * WCAG AA needs 4.5 for body text, 3 for large text and UI components.
 */
export function contrastRatio(foreground, background) {
  const a = relativeLuminance(foreground);
  const b = relativeLuminance(background);
  if (a === null || b === null) return null;

  const lightest = Math.max(a, b);
  const darkest = Math.min(a, b);
  return (lightest + 0.05) / (darkest + 0.05);
}

/**
 * Pick a readable text colour for the given background.
 *
 * The light colour is kept as long as it reaches `minRatio` — white on a brand colour is the
 * convention, so it only gives way once it is genuinely hard to read. Pass `minRatio: null`
 * to always take the highest contrast instead. Unparseable backgrounds get the light colour.
 */
export function readableTextColor(background, { light = LIGHT, dark = DARK, minRatio = 3 } = {}) {
  const onLight = contrastRatio(light, background);
  const onDark = contrastRatio(dark, background);
  if (onLight === null || onDark === null) return light;

  if (minRatio !== null && onLight >= minRatio) return light;
  return onDark > onLight ? dark : light;
}

/**
 * Whether a foreground/background pair meets a WCAG level.
 * Sizes: 'normal' (AA 4.5, AAA 7) and 'large' (AA 3, AAA 4.5).
 */
export function meetsContrast(foreground, background, { level = 'AA', size = 'normal' } = {}) {
  const ratio = contrastRatio(foreground, background);
  if (ratio === null) return false;

  const thresholds = {
    AA: { normal: 4.5, large: 3 },
    AAA: { normal: 7, large: 4.5 },
  };

  return ratio >= (thresholds[level]?.[size] ?? thresholds.AA.normal);
}
